<template>
    <div class="fm-page">
        <!-- Header -->
        <div class="fm-header">
            <div class="fm-header-left">
                <div class="fm-icon-wrap"><i class="fas fa-folder-open"></i></div>
                <div>
                    <h2 class="fm-title">File Manager</h2>
                    <span class="fm-subtitle">{{ filteredFolders.length }} folder{{ filteredFolders.length !== 1 ? 's' : '' }}, {{ filteredFiles.length }} file{{ filteredFiles.length !== 1 ? 's' : '' }}</span>
                </div>
            </div>
            <div class="fm-header-actions">
                <button class="fm-action-btn fm-btn-outline" @click="openFolderCreate"><i class="fas fa-folder-plus"></i> New Folder</button>
                <button class="fm-action-btn fm-btn-primary" @click="openFileCreate"><i class="fas fa-cloud-upload-alt"></i> Upload File</button>
            </div>
        </div>

        <!-- Toolbar: Filters + View toggles -->
        <div class="fm-toolbar">
            <div class="fm-filters">
                <div class="fm-filter-field">
                    <SelectDropdown
                        :id="`form-employeeId`"
                        :options="employees.map((item) => ({ id: item.id, title: item.fullName }))"
                        :multiple="false"
                        :field="employeeId"
                        :value="employeeId"
                        @change="employeeId = $event"
                        title="Select Employee"
                    />
                </div>
                <div class="fm-filter-field fm-search-field">
                    <div class="fm-search-input-wrap">
                        <i class="fas fa-search fm-search-icon"></i>
                        <input class="fm-search-input" type="text" placeholder="Search files/folders..." v-model="searchText" @keyup.enter="searchFilesFolders" />
                    </div>
                </div>
                <div class="fm-filter-btns">
                    <button class="fm-filter-btn fm-btn-search" @click="searchFilesFolders"><i class="fas fa-search"></i></button>
                    <button class="fm-filter-btn fm-btn-clear" @click="clearFilters" v-if="searchText || employeeId"><i class="fas fa-times"></i></button>
                </div>
            </div>
            <div class="fm-view-controls">
                <div class="fm-type-pills">
                    <button v-for="t in fileSelectionTypes" :key="t.value" :class="['fm-pill', { active: activeFileSelectionType === t.value }]" @click="handleFileSelectionTypeChange(t.value)">{{ t.title }}</button>
                </div>
                <div class="fm-layout-pills">
                    <button :class="['fm-layout-btn', { active: !listMode }]" @click="activeFileLayoutType = FILE_LAYOUT_TYPES.GRID" title="Grid"><i class="fas fa-th-large"></i></button>
                    <button :class="['fm-layout-btn', { active: listMode }]" @click="activeFileLayoutType = FILE_LAYOUT_TYPES.LIST" title="List"><i class="fas fa-list"></i></button>
                </div>
            </div>
        </div>

        <!-- Breadcrumb / Back -->
        <div class="fm-breadcrumb" v-if="openedFolders.length > 0">
            <button class="fm-back-btn" @click="goBackFolder"><i class="fas fa-arrow-left"></i> Back</button>
            <div class="fm-breadcrumb-trail">
                <span class="fm-crumb" @click="goToRoot">Root</span>
                <span class="fm-crumb-sep" v-for="(f, i) in openedFolders" :key="f.id">
                    <i class="fas fa-chevron-right"></i>
                    <span class="fm-crumb" :class="{ current: i === openedFolders.length - 1 }" @click="goToFolderIndex(i)">{{ f.name }}</span>
                </span>
            </div>
        </div>

        <div class="fm-content-wrap">
            <!-- Main Content -->
            <div class="fm-main">
                <!-- Loading -->
                <div v-if="searching" class="fm-loading">
                    <i class="fas fa-spinner fa-spin"></i> Loading...
                </div>

                <!-- Empty -->
                <div v-else-if="!filteredFolders.length && !filteredFiles.length" class="fm-empty">
                    <div class="fm-empty-icon"><i class="fas fa-folder-open"></i></div>
                    <p class="fm-empty-title">No Results Found</p>
                    <p class="fm-empty-desc">This location is empty. Create a folder or upload a file to get started.</p>
                </div>

                <!-- Grid View -->
                <div v-else-if="!listMode" class="fm-grid">
                    <FileFolderItem
                        v-for="folder of filteredFolders" :key="'f-'+folder.id"
                        :listMode="false"
                        @openInfo="handleShowFileInfo"
                        @openFolder="handleOpenFolder(folder)"
                        :folder="folder"
                    />
                    <FileFolderItem
                        v-for="file of filteredFiles" :key="'fi-'+file.id"
                        :listMode="false"
                        @openInfo="handleShowFileInfo"
                        :file="file"
                    />
                </div>

                <!-- List View -->
                <div v-else class="fm-list-view">
                    <div class="fm-list-head">
                        <span class="fm-list-col-name">Name</span>
                        <span class="fm-list-col-date">Date</span>
                        <span class="fm-list-col-size">Size</span>
                        <span class="fm-list-col-act"></span>
                    </div>
                    <FileFolderItem
                        v-for="folder of filteredFolders" :key="'f-'+folder.id"
                        :listMode="true"
                        @openInfo="handleShowFileInfo"
                        @openFolder="handleOpenFolder(folder)"
                        :folder="folder"
                    />
                    <FileFolderItem
                        v-for="file of filteredFiles" :key="'fi-'+file.id"
                        :listMode="true"
                        @openInfo="handleShowFileInfo"
                        :file="file"
                    />
                </div>
            </div>

            <!-- Info Panel -->
            <div class="fm-info-panel" v-if="showFileInfo && infoData">
                <FileFolderInfo :infoData="infoData" @close="showFileInfo = false" />
            </div>
        </div>

        <!-- Create Modal -->
        <CustomModal
            v-if="showCreateModal"
            :title="`${fileCreateMode ? 'Upload File' : 'Create Folder'}`"
            :show="showCreateModal"
            @close="showCreateModal = false"
        >
            <div class="fm-modal-body">
                <div class="fm-modal-field">
                    <label class="fm-label">Employee <span class="fm-req">*</span></label>
                    <SelectDropdown
                        :id="`form-employeeId2`"
                        :options="employees.map((item) => ({ id: item.id, title: item.fullName }))"
                        :multiple="false"
                        :field="employeeIdForCreation"
                        :value="employeeIdForCreation"
                        @change="employeeIdForCreation = $event"
                        title="Select Employee"
                    />
                </div>
                <div v-if="fileCreateMode">
                    <div class="fm-modal-field">
                        <label class="fm-label">File Name <span class="fm-req">*</span></label>
                        <input class="fm-input" placeholder="Enter file name" v-model="fileName" type="text" />
                    </div>
                    <div class="fm-modal-field">
                        <label class="fm-label">Select File <span class="fm-req">*</span></label>
                        <CustomInput placeholder="" v-model="selectedFile" label="" type="file" />
                    </div>
                </div>
                <div v-else>
                    <div class="fm-modal-field">
                        <label class="fm-label">Folder Name <span class="fm-req">*</span></label>
                        <input class="fm-input" placeholder="Enter folder name" v-model="folderName" type="text" />
                    </div>
                </div>
                <button class="fm-action-btn fm-btn-primary fm-modal-submit" @click="handleFileFolderCreate" :disabled="processing">
                    <i :class="processing ? 'fas fa-spinner fa-spin' : fileCreateMode ? 'fas fa-upload' : 'fas fa-folder-plus'"></i>
                    {{ processing ? 'Please wait...' : fileCreateMode ? 'Upload File' : 'Create Folder' }}
                </button>
            </div>
        </CustomModal>
    </div>
</template>

<script>
import CustomInput from "../../components/crud/custom-input.vue"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import FileFolderInfo from "../../components/file-manager/FileFolderInfo.vue"
import FileFolderItem from "../../components/file-manager/FileFolderItem.vue"
import CustomModal from "../../components/layout/CustomModal.vue"
import { http } from "../../utils/http"

const FILE_SELECTION_TYPES = {
    ALL: "ALL",
    FILES: "FILES",
    FOLDERS: "FOLDERS",
}
const FILE_LAYOUT_TYPES = {
    GRID: "GRID",
    LIST: "LIST",
}

export default {
    components: {
        FileFolderInfo,
        CustomModal,
        CustomInput,
        FileFolderItem,
        SelectDropdown,
    },
    data() {
        return {
            searchText: "",
            infoData: null,
            employeeId: "",
            employeeIdForCreation: "",
            folders: [],
            files: [],
            showFileInfo: false,
            employees: [],
            searching: false,
            showCreateModal: false,
            selectedFile: null,
            folderName: "",
            fileName: "",
            fileCreateMode: false,
            openedFolders: [],
            processing: false,
            FILE_SELECTION_TYPES,
            FILE_LAYOUT_TYPES,
            fileSelectionTypes: [
                { title: "All", value: FILE_SELECTION_TYPES.ALL },
                { title: "Files", value: FILE_SELECTION_TYPES.FILES },
                { title: "Folders", value: FILE_SELECTION_TYPES.FOLDERS },
            ],
            activeFileSelectionType: FILE_SELECTION_TYPES.ALL,
            activeFileLayoutType: FILE_LAYOUT_TYPES.GRID,
        }
    },
    mounted() {
        this.getEmployeesForSelection()
        this.searchFilesFolders()
    },
    computed: {
        listMode() {
            return this.activeFileLayoutType == FILE_LAYOUT_TYPES.LIST
        },
        openedFolderId() {
            const res = this.openedFolders && this.openedFolders.length ? this.openedFolders[this.openedFolders.length - 1] : {}
            return res.id || ""
        },
        filteredFolders() {
            switch (this.activeFileSelectionType) {
                case FILE_SELECTION_TYPES.ALL:
                case FILE_SELECTION_TYPES.FOLDERS:
                    return this.folders
                case FILE_SELECTION_TYPES.FILES:
                    return []
            }
            return []
        },
        filteredFiles() {
            switch (this.activeFileSelectionType) {
                case FILE_SELECTION_TYPES.ALL:
                case FILE_SELECTION_TYPES.FILES:
                    return this.files
                case FILE_SELECTION_TYPES.FOLDERS:
                    return []
            }
            return []
        },
    },
    methods: {
        clearFilters() {
            this.searchText = ""
            this.employeeId = ""
            this.searchFilesFolders()
        },
        handleFileSelectionTypeChange(val) {
            this.activeFileSelectionType = val
        },
        handleShowFileInfo(infoData) {
            this.showFileInfo = true
            this.infoData = infoData
        },
        goBackFolder() {
            this.openedFolders = this.openedFolders.slice(0, this.openedFolders.length - 1)
            this.searchFilesFolders()
        },
        goToRoot() {
            this.openedFolders = []
            this.searchFilesFolders()
        },
        goToFolderIndex(index) {
            this.openedFolders = this.openedFolders.slice(0, index + 1)
            this.searchFilesFolders()
        },
        handleOpenFolder(folderData) {
            this.openedFolders.push(folderData)
            this.searchFilesFolders()
            this.showFileInfo = false
        },
        handleFileFolderCreate() {
            if (this.fileCreateMode) { this.uploadFile() } else { this.createFolder() }
        },
        async getEmployeesForSelection() {
            try {
                const res = await http.get(`/employees?limited_fields=true`)
                this.employees = res.data.result
            } catch (err) { console.log("error in getting employees", err) }
        },
        async searchFilesFolders() {
            try {
                this.searching = true
                const res = await http.get(
                    `/file-management/search?search=${this.searchText}&employee_id=${this.employeeId}&parentFolderId=${this.openedFolderId || ""}`
                )
                this.folders = res.data.result.folders
                this.files = res.data.result.files
            } catch (err) { console.log("error in getting files folders", err) }
            this.searching = false
        },
        openFolderCreate() {
            this.showCreateModal = true
            this.fileCreateMode = false
            this.showFileInfo = false
        },
        openFileCreate() {
            this.showCreateModal = true
            this.fileCreateMode = true
            this.showFileInfo = false
        },
        async createFolder() {
            try {
                if (!this.folderName || !this.employeeIdForCreation) return this.$toast.error("Fill up all fields")
                this.processing = true
                await http.post(`/file-management/folders`, { name: this.folderName, userId: this.employeeIdForCreation, parentFolderId: this.openedFolderId })
                this.$toast.success(`Folder created successfully`)
                this.showCreateModal = false
                this.folderName = ""
                window.location.reload()
            } catch (err) { console.log("error in creating folder", err); this.$toast.error("Could not create!") }
            this.processing = false
        },
        async uploadFile() {
            try {
                if (!this.fileName || !this.selectedFile || !this.employeeIdForCreation) return this.$toast.error("Please fill up all the fields")
                this.processing = true
                const formData = new FormData()
                formData.append("name", this.fileName)
                formData.append("file", this.selectedFile)
                formData.append("userId", this.employeeIdForCreation)
                formData.append("folderId", this.openedFolderId)
                await http.post(`/file-management/files`, formData)
                this.$toast.success(`File uploaded successfully`)
                this.showCreateModal = false
                this.folderName = ""
                window.location.reload()
            } catch (err) { console.log("error in uploading file", err); this.$toast.error("Could not create!") }
            this.processing = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.fm-page { animation: fmFade .3s ease; }
@keyframes fmFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.fm-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.fm-header-left { display:flex; align-items:center; gap:10px; }
.fm-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.fm-title { font-size:15px; font-weight:700; color:$gray800; margin:0; line-height:1.2; }
.fm-subtitle { font-size:11.5px; color:$gray400; font-weight:500; }
.fm-header-actions { display:flex; gap:8px; }

/* Buttons */
.fm-action-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:6px; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; border:none; i { font-size:11px; } }
.fm-btn-primary { background:$primaryColor; color:white; &:hover { background:$primaryDark; } &:disabled { opacity:0.6; cursor:not-allowed; } }
.fm-btn-outline { background:white; color:$gray700; border:1px solid $gray200; &:hover { background:$gray50; border-color:$gray300; } }

/* Toolbar */
.fm-toolbar { background:white; border:1px solid $gray100; border-radius:8px; padding:12px 16px; margin-bottom:12px; box-shadow:$shadowSm; display:flex; flex-direction:column; gap:12px; }
.fm-filters { display:flex; align-items:flex-end; gap:10px; flex-wrap:wrap; }
.fm-filter-field { flex:1; min-width:160px; }
.fm-search-field { flex:2; }
.fm-search-input-wrap { position:relative; }
.fm-search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); font-size:12px; color:$gray400; }
.fm-search-input { width:100%; padding:8px 12px 8px 30px; border:1px solid $gray200; border-radius:6px; font-size:13px; font-weight:500; color:$gray800; font-family:inherit; outline:none; transition:all $transitionFast; &:focus { border-color:$primaryColor; box-shadow:0 0 0 3px rgba(0,94,137,0.08); } &::placeholder { color:$gray300; } }
.fm-filter-btns { display:flex; gap:6px; padding-bottom:2px; }
.fm-filter-btn { width:34px; height:34px; border-radius:6px; border:1px solid $gray200; background:white; color:$gray500; display:flex; align-items:center; justify-content:center; font-size:12px; cursor:pointer; transition:all $transitionFast; }
.fm-btn-search { &:hover { background:$primaryColor; color:white; border-color:$primaryColor; } }
.fm-btn-clear { &:hover { background:#fee2e2; color:#dc2626; border-color:#fecaca; } }

/* View Controls */
.fm-view-controls { display:flex; align-items:center; justify-content:space-between; gap:10px; }
.fm-type-pills { display:flex; gap:4px; }
.fm-pill { padding:5px 14px; border-radius:$radiusFull; font-size:12px; font-weight:600; border:1px solid $gray200; background:white; color:$gray500; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$gray50; color:$gray700; } &.active { background:$primaryColor; color:white; border-color:$primaryColor; } }
.fm-layout-pills { display:flex; border:1px solid $gray200; border-radius:6px; overflow:hidden; }
.fm-layout-btn { width:34px; height:30px; display:flex; align-items:center; justify-content:center; background:white; border:none; color:$gray400; cursor:pointer; font-size:12px; transition:all $transitionFast; &:hover { color:$gray600; } &.active { background:$primaryColor; color:white; } &+.fm-layout-btn { border-left:1px solid $gray200; } }

/* Breadcrumb */
.fm-breadcrumb { display:flex; align-items:center; gap:10px; background:white; border:1px solid $gray100; border-radius:8px; padding:8px 14px; margin-bottom:12px; box-shadow:$shadowSm; }
.fm-back-btn { display:inline-flex; align-items:center; gap:5px; padding:5px 10px; border:1px solid $gray200; border-radius:6px; background:white; color:$gray600; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit; transition:all $transitionFast; &:hover { background:$gray50; } i { font-size:10px; } }
.fm-breadcrumb-trail { display:flex; align-items:center; gap:4px; font-size:12.5px; color:$gray400; flex-wrap:wrap; }
.fm-crumb { font-weight:600; color:$gray500; cursor:pointer; transition:color $transitionFast; &:hover { color:$primaryColor; } &.current { color:$gray800; cursor:default; } }
.fm-crumb-sep { display:inline-flex; align-items:center; gap:4px; i { font-size:9px; color:$gray300; } }

/* Content */
.fm-content-wrap { display:flex; gap:16px; }
.fm-main { flex:1; min-width:0; }
.fm-info-panel { flex:0 0 280px; }

/* Grid */
.fm-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(180px, 1fr)); gap:10px; }

/* List view */
.fm-list-view { background:white; border:1px solid $gray100; border-radius:8px; overflow:hidden; box-shadow:$shadowSm; }
.fm-list-head { display:grid; grid-template-columns:1fr 120px 100px 40px; padding:8px 14px; background:$gray50; border-bottom:1px solid $gray100; font-size:11px; font-weight:600; color:$gray500; text-transform:uppercase; letter-spacing:0.3px; }
.fm-list-col-name { } .fm-list-col-date { text-align:right; } .fm-list-col-size { text-align:right; } .fm-list-col-act { text-align:right; }

/* Loading */
.fm-loading { text-align:center; padding:48px 20px; color:$gray400; font-size:13px; font-weight:500; i { margin-right:6px; } }

/* Empty */
.fm-empty { text-align:center; padding:56px 20px; background:white; border:1px solid $gray100; border-radius:10px; }
.fm-empty-icon { width:52px; height:52px; border-radius:$radiusFull; background:$gray50; color:$gray300; display:flex; align-items:center; justify-content:center; font-size:22px; margin:0 auto 12px; }
.fm-empty-title { font-size:14px; font-weight:700; color:$gray700; margin:0 0 4px; }
.fm-empty-desc { font-size:12.5px; color:$gray400; margin:0; max-width:320px; margin:0 auto; }

/* Modal */
.fm-modal-body { padding:8px 0; display:flex; flex-direction:column; gap:12px; }
.fm-modal-field { display:flex; flex-direction:column; gap:4px; }
.fm-label { font-size:12px; font-weight:600; color:$gray600; }
.fm-req { color:#dc2626; }
.fm-input { width:100%; padding:8px 12px; border:1px solid $gray200; border-radius:6px; font-size:13px; font-weight:500; color:$gray800; font-family:inherit; outline:none; transition:all $transitionFast; &:focus { border-color:$primaryColor; box-shadow:0 0 0 3px rgba(0,94,137,.08); } &::placeholder { color:$gray300; } }
.fm-modal-submit { width:100%; justify-content:center; margin-top:4px; padding:9px; }

@media(max-width:768px) {
    .fm-header { flex-direction:column; align-items:stretch; }
    .fm-header-actions { justify-content:flex-end; }
    .fm-filters { flex-direction:column; }
    .fm-filter-field { min-width:auto; }
    .fm-content-wrap { flex-direction:column; }
    .fm-info-panel { flex:auto; }
    .fm-grid { grid-template-columns:repeat(auto-fill, minmax(140px, 1fr)); }
}
</style>
